/**
 * Metadata Redux-Saga middleware.
 *
 * Listens for the `"UPDATE_METADATA"` action dispatched by
 * `metadata-handler.ts` after a successful `/metadata` API call and:
 * 1. Writes the full metadata payload to the Redux `metadata` slice.
 * 2. Resolves the user's default watchlist and selects it.
 * 3. Cross-references the selected watchlist's scrip IDs with the feed
 *    instrument catalogue and populates the `watchlist` slice with the
 *    full scrip objects (symbol, ID, segment).
 */
import { call, put, takeLatest, select } from "redux-saga/effects";

import { metadataReducer } from "@store/metadata-slice";
import {
  addScripsReducer,
  addScripIdsReducer,
  updateSelectedWatchlistReducer,
} from "@store/watchlist-slice";
import { createWatchlistInAmibrokerHandler } from "@service/watchlist-handler";

export function* updateMetadataSaga({ payload: { metadata }, type }) {
  yield put(metadataReducer(metadata));

  // Push symbolScripId map and all watchlists to AmiBroker if enabled.
  // Done BEFORE the early return so it always runs regardless of default watchlist.
  try {
    const amiEnabled: boolean = yield select((state: any) => state.amibroker?.isEnabled || false);
    if (amiEnabled) {
      const symbolScripId: Record<string, string> =
        (metadata.feedsScrips || []).reduce((acc: Record<string, string>, feed: any) => {
          (feed?.scrips || []).forEach((scrip: any) => { acc[String(scrip.id)] = scrip.symbol; });
          return acc;
        }, {} as Record<string, string>);

      if (Object.keys(symbolScripId).length > 0) {
        const { pushSymbolScripIdMap } = require("../service/ipc-handler");
        pushSymbolScripIdMap(symbolScripId); // fire-and-forget
      }

      // Push all watchlists (feedsScrips + userWatchlist + defaultWatchlist)
      yield call(createWatchlistInAmibrokerHandler, {
        feedsScrips: metadata.feedsScrips || [],
        userWatchlist: metadata.userWatchlist || [],
        defaultWatchlist: metadata.defaultWatchlist || [],
      });
    }
  } catch {
    // Silent — AmiBroker sync is non-critical
  }

  if (!metadata?.userDefaultWatchlist?.watchlistId) {
    return;
  }

  let selectedWatchlist;
  (metadata.userWatchlist || []).forEach((watchlist) => {
    if (watchlist.id === metadata.userDefaultWatchlist.watchlistId) {
      selectedWatchlist = {
        value: watchlist.id,
        label: watchlist.name,
        scrips: watchlist.scrips,
      };
    }
  });
  (metadata.defaultWatchlist || []).forEach((watchlist) => {
    if (watchlist.id === metadata.userDefaultWatchlist.watchlistId) {
      selectedWatchlist = {
        value: watchlist.id,
        label: watchlist.name,
        scrips: watchlist.scrips,
      };
    }
  });

  const scrips = (metadata.feedsScrips || [])
    .flatMap((feed) =>
      (feed?.scrips || []).map((scrip) => ({ ...scrip, segment: feed.name }))
    )
    .filter((scrip) => selectedWatchlist?.scrips?.includes(scrip.id))
    .map((scrip) => ({
      symbol: scrip.symbol,
      id: scrip.id,
      segment: scrip.segment,
    }));

  yield put(updateSelectedWatchlistReducer(selectedWatchlist));
  yield put(addScripsReducer(scrips));
  yield put(addScripIdsReducer(scrips.map((scrip) => scrip.id)));
}

export function* metadataWatcher() {
  yield takeLatest("UPDATE_METADATA", updateMetadataSaga);
}
